import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { setHideNoEvRows } from '../utils/searchDisplay';

const SiteConfigContext = createContext({
  introSequenceEnabled: false,
  communityScreenEnabled: false,
  dfsConsensusExclusionEnabled: true,
  oneSidedJuicePolicy: 'market_typical',
  newResultsUiEnabled: false,
  newUiShowEverywhere: false,
  sgpMathV2Enabled: true,
  // site_config/search_display.hideNoEvRows — missing = false (show every row).
  hideNoEvRows: false,
  loading: true,
});

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState({
    introSequenceEnabled: false,
    communityScreenEnabled: false,
    dfsConsensusExclusionEnabled: true,
    oneSidedJuicePolicy: 'market_typical',
    newResultsUiEnabled: false,
    newUiShowEverywhere: false,
    sgpMathV2Enabled: true,
    hideNoEvRows: false,
    loading: true,
  });

  useEffect(() => {
    const ref = doc(db, 'site_config', 'feature_flags');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        setConfig(prev => ({
          ...prev,
          introSequenceEnabled: data.introSequenceEnabled === true,
          communityScreenEnabled: data.communityScreenEnabled === true,
          // absent = true (legacy default)
          dfsConsensusExclusionEnabled: data.dfsConsensusExclusionEnabled !== false,
          oneSidedJuicePolicy: data.oneSidedJuicePolicy || 'market_typical',
          newResultsUiEnabled: data.newResultsUiEnabled === true,
          // When OFF (default): new UI only shows on staging/dev.
          // When ON: new UI shows to all users on production too.
          newUiShowEverywhere: data.newUiShowEverywhere === true,
          sgpMathV2Enabled: data.sgpMathV2Enabled !== false,
          loading: false,
        }));
      },
      () => {
        setConfig(prev => ({ ...prev, loading: false }));
      }
    );
    return () => unsub();
  }, []);

  // Search display switch (same doc the server reads; utils/searchDisplay.js).
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'site_config', 'search_display'),
      (snap) => {
        const hide = (snap.data() || {}).hideNoEvRows === true;
        setHideNoEvRows(hide);
        setConfig(prev => ({ ...prev, hideNoEvRows: hide }));
      },
      () => { /* keep the default: show everything */ }
    );
    return () => unsub();
  }, []);

  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
